<template>
  <div class="jim-scheduling-container pricing-admin-page">

    <!-- Page header row -->
    <div class="page-header-row">
      <div class="page-header-left"></div>
      <h1 class="page-heading">PRICING INCREASE ADMIN</h1>
      <div class="page-header-right"></div>
    </div>

    <div class="main-content">

      <!-- LEFT SIDE CONTROLS -->
      <div class="left-side-section">

        <div class="control" style="width:100%;">
          <label>Company DB</label>
          <input
            v-model="companyDb"
            type="text"
            disabled
            title="Currently locked to Macola LIVE company 100"
          />
        </div>

        <div class="control" style="width:100%;">
          <label>Percent Increase</label>
          <input
            v-model="pct"
            type="text"
            placeholder="ex: 8"
            @keyup.enter="previewAllActivePricing"
          />
        </div>

        <div class="control" style="width:100%;">
          <label>Effective Date</label>
          <input
            v-model="effectiveDt"
            type="date"
          />
        </div>

        <button
          class="plan-it-btn"
          :disabled="previewLoading || !inputsValid"
          @click="previewAllActivePricing"
        >
          {{ previewLoading ? "Previewing..." : "Preview Active" }}
        </button>

        <button
          class="plan-it-btn danger-btn"
          :disabled="expireLoading || !canExpire"
          @click="openExpireConfirm"
        >
          {{ expireLoading ? "Expiring..." : "Expire Current" }}
        </button>

        <button
          class="plan-it-btn"
          :disabled="expiredPreviewLoading || !canPreviewExpired"
          @click="previewExpiredPricing"
        >
          {{ expiredPreviewLoading ? "Previewing..." : "Preview Expired" }}
        </button>

        <button
          class="plan-it-btn danger-btn"
          :disabled="insertLoading || !canInsert"
          @click="openInsertConfirm"
        >
          {{ insertLoading ? "Inserting..." : "Insert New" }}
        </button>

        <button
          class="plan-it-btn"
          :disabled="finalPreviewLoading || !effectiveDt"
          @click="previewFinalActivePricing"
        >
          {{ finalPreviewLoading ? "Checking..." : "Preview Final" }}
        </button>

        <button
          class="plan-it-btn"
          :disabled="anyLoading"
          @click="resetPage"
        >
          Reset
        </button>

        <button
          class="plan-it-btn"
          :disabled="exportLoading"
          @click="exportActivePricingExcel"
        >
          {{ exportLoading ? "Exporting..." : "Export Excel" }}
        </button>

        <div v-if="error" class="panel error-box" style="margin-top:10px;">
          {{ error }}
        </div>

        <div v-if="success" class="panel success-box" style="margin-top:10px;">
          {{ success }}
        </div>

      </div>

      <!-- RIGHT SIDE CONTENT -->
      <div class="right-side-section">

        <!-- Warning / purpose panel -->
        <div class="panel warning-panel">
          <div class="warning-title">Play Company Test Mode</div>
          <div class="warning-body">
            This page is intended to test the global price increase process against Macola company
            <b>{{ companyDb }}</b>. It does not export pricing rows. It only previews counts and sample rows,
            then lets you expire and insert pricing through controlled steps.
          </div>
        </div>

        <!-- Status bar -->
        <div class="summary-bar">
          <div class="summary-item">
            <div class="summary-label">DB</div>
            <div class="summary-value">{{ companyDb }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Increase</div>
            <div class="summary-value">{{ formattedPct }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Effective</div>
            <div class="summary-value">{{ effectiveDt || "-" }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Old Ends</div>
            <div class="summary-value">{{ oldEndDateLabel }}</div>
          </div>
        </div>

        <!-- Workflow step tracker -->
        <div class="panel step-panel">
          <div class="step-row">
            <div class="step" :class="{ done: !!activePreview, active: currentStep === 1 }">
              <div class="step-num">1</div>
              <div>
                <div class="step-title">Preview Active</div>
                <div class="step-sub">Review active rows before writes.</div>
              </div>
            </div>

            <div class="step" :class="{ done: expiredDone, active: currentStep === 2 }">
              <div class="step-num">2</div>
              <div>
                <div class="step-title">Expire Current</div>
                <div class="step-sub">Set active rows to end before effective date.</div>
              </div>
            </div>

            <div class="step" :class="{ done: !!expiredPreview, active: currentStep === 3 }">
              <div class="step-num">3</div>
              <div>
                <div class="step-title">Preview Expired</div>
                <div class="step-sub">Review rows that will be cloned.</div>
              </div>
            </div>

            <div class="step" :class="{ done: insertedDone, active: currentStep === 4 }">
              <div class="step-num">4</div>
              <div>
                <div class="step-title">Insert New</div>
                <div class="step-sub">Insert 2040 active rows with increase.</div>
              </div>
            </div>

            <div class="step" :class="{ done: !!finalPreview, active: currentStep === 5 }">
              <div class="step-num">5</div>
              <div>
                <div class="step-title">Preview Final</div>
                <div class="step-sub">Confirm new active rows exist.</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Scrollable content -->
        <div class="sheet-scroll-container">

          <!-- Active Preview -->
          <div v-if="activePreview" class="panel preview-panel">
            <div class="sheet-page-header">
              <h2>Active Pricing Preview</h2>
              <div class="small-muted">Before expiration</div>
            </div>

            <div class="summary-totals-bar">
              <div class="tot">
                <div class="k">Active Rows</div>
                <div class="v">{{ formatNumber(activePreview.active_count) }}</div>
              </div>

              <div class="tot">
                <div class="k">Future Rows</div>
                <div class="v" :class="{ badText: Number(activePreview.future_count || 0) > 0 }">
                  {{ formatNumber(activePreview.future_count) }}
                </div>
              </div>

              <div class="tot">
                <div class="k">Customers</div>
                <div class="v">{{ formatNumber(activePreview.distinct_customers) }}</div>
              </div>

              <div class="tot">
                <div class="k">Items</div>
                <div class="v">{{ formatNumber(activePreview.distinct_items) }}</div>
              </div>

              <div class="tot">
                <div class="k">Prod Cats</div>
                <div class="v">{{ formatNumber(activePreview.distinct_prod_cats) }}</div>
              </div>
            </div>

            <div
              v-if="Number(activePreview.future_count || 0) > 0"
              class="panel error-box inline-message"
            >
              Future effective pricing rows were found. The expire route should block until these are reviewed.
            </div>

            <div class="table-scroll">
              <table class="popup-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Item</th>
                    <th>Prod Cat</th>
                    <th>Basis</th>
                    <th>Start</th>
                    <th>End</th>
                    <th style="text-align:right;">Old 1</th>
                    <th style="text-align:right;">New 1</th>
                    <th style="text-align:right;">Old 2</th>
                    <th style="text-align:right;">New 2</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="r in activePreview.sample"
                    :key="'active-' + rowKey(r)"
                  >
                    <td>{{ r.cust_no }}</td>
                    <td>{{ r.item_no }}</td>
                    <td>{{ r.prod_cat }}</td>
                    <td>{{ r.cd_prc_basis }}</td>
                    <td>{{ formatDate(r.start_dt) }}</td>
                    <td>{{ formatDate(r.end_dt) }}</td>
                    <td style="text-align:right;">{{ formatMoney(r.old_prc_1) }}</td>
                    <td style="text-align:right;">{{ formatMoney(r.new_prc_1) }}</td>
                    <td style="text-align:right;">{{ formatMoney(r.old_prc_2) }}</td>
                    <td style="text-align:right;">{{ formatMoney(r.new_prc_2) }}</td>
                  </tr>

                  <tr v-if="!activePreview.sample || activePreview.sample.length === 0">
                    <td colspan="10" class="empty-row">
                      No sample rows returned.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Expired Preview -->
          <div v-if="expiredPreview" class="panel preview-panel">
            <div class="sheet-page-header">
              <h2>Expired Pricing Preview</h2>
              <div class="small-muted">Rows ready to clone</div>
            </div>

            <div class="summary-totals-bar">
              <div class="tot">
                <div class="k">Expired Rows</div>
                <div class="v">{{ formatNumber(expiredPreview.expired_count) }}</div>
              </div>

              <div class="tot">
                <div class="k">Already New Active</div>
                <div class="v" :class="{ badText: Number(expiredPreview.active_new_count || 0) > 0 }">
                  {{ formatNumber(expiredPreview.active_new_count) }}
                </div>
              </div>

              <div class="tot">
                <div class="k">Customers</div>
                <div class="v">{{ formatNumber(expiredPreview.distinct_customers) }}</div>
              </div>

              <div class="tot">
                <div class="k">Items</div>
                <div class="v">{{ formatNumber(expiredPreview.distinct_items) }}</div>
              </div>

              <div class="tot">
                <div class="k">Prod Cats</div>
                <div class="v">{{ formatNumber(expiredPreview.distinct_prod_cats) }}</div>
              </div>
            </div>

            <div
              v-if="Number(expiredPreview.active_new_count || 0) > 0"
              class="panel error-box inline-message"
            >
              New active pricing already exists for this effective date. Insert should be blocked.
            </div>

            <div class="table-scroll">
              <table class="popup-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Item</th>
                    <th>Prod Cat</th>
                    <th>Basis</th>
                    <th>Old Start</th>
                    <th>Old End</th>
                    <th>New Start</th>
                    <th>New End</th>
                    <th style="text-align:right;">Old 1</th>
                    <th style="text-align:right;">New 1</th>
                    <th style="text-align:right;">Old 2</th>
                    <th style="text-align:right;">New 2</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="r in expiredPreview.sample"
                    :key="'expired-' + rowKey(r)"
                  >
                    <td>{{ r.cust_no }}</td>
                    <td>{{ r.item_no }}</td>
                    <td>{{ r.prod_cat }}</td>
                    <td>{{ r.cd_prc_basis }}</td>
                    <td>{{ formatDate(r.old_start_dt) }}</td>
                    <td>{{ formatDate(r.old_end_dt) }}</td>
                    <td>{{ formatDate(r.new_start_dt) }}</td>
                    <td>{{ formatDate(r.new_end_dt) }}</td>
                    <td style="text-align:right;">{{ formatMoney(r.old_prc_1) }}</td>
                    <td style="text-align:right;">{{ formatMoney(r.new_prc_1) }}</td>
                    <td style="text-align:right;">{{ formatMoney(r.old_prc_2) }}</td>
                    <td style="text-align:right;">{{ formatMoney(r.new_prc_2) }}</td>
                  </tr>

                  <tr v-if="!expiredPreview.sample || expiredPreview.sample.length === 0">
                    <td colspan="12" class="empty-row">
                      No sample rows returned.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Final Preview -->
          <div v-if="finalPreview" class="panel preview-panel">
            <div class="sheet-page-header">
              <h2>Final Active Pricing Preview</h2>
              <div class="small-muted">After new rows inserted</div>
            </div>

            <div class="summary-totals-bar">
              <div class="tot">
                <div class="k">Final Active Rows</div>
                <div class="v">{{ formatNumber(finalPreview.final_active_count) }}</div>
              </div>

              <div class="tot">
                <div class="k">Old Expired Rows</div>
                <div class="v">{{ formatNumber(finalPreview.old_expired_count) }}</div>
              </div>

              <div class="tot">
                <div class="k">Effective Date</div>
                <div class="v">{{ finalPreview.effective_dt }}</div>
              </div>
            </div>

            <div class="table-scroll">
              <table class="popup-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Item</th>
                    <th>Prod Cat</th>
                    <th>Basis</th>
                    <th>Start</th>
                    <th>End</th>
                    <th style="text-align:right;">Price 1</th>
                    <th style="text-align:right;">Price 2</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="r in finalPreview.sample"
                    :key="'final-' + rowKey(r)"
                  >
                    <td>{{ r.cust_no }}</td>
                    <td>{{ r.item_no }}</td>
                    <td>{{ r.prod_cat }}</td>
                    <td>{{ r.cd_prc_basis }}</td>
                    <td>{{ formatDate(r.start_dt) }}</td>
                    <td>{{ formatDate(r.end_dt) }}</td>
                    <td style="text-align:right;">{{ formatMoney(r.prc_or_disc_1) }}</td>
                    <td style="text-align:right;">{{ formatMoney(r.prc_or_disc_2) }}</td>
                  </tr>

                  <tr v-if="!finalPreview.sample || finalPreview.sample.length === 0">
                    <td colspan="8" class="empty-row">
                      No final active sample rows returned.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-if="!activePreview && !expiredPreview && !finalPreview"
            class="panel empty-state"
          >
            Enter the percent increase and effective date, then click
            <b>Preview Active</b>. No pricing rows will be changed until you explicitly click
            <b>Expire Current</b> and then <b>Insert New</b>.
          </div>

        </div>
      </div>

      <!-- Expire Confirmation Modal -->
      <div v-if="showExpireConfirm" class="popup-overlay" @click.self="closeExpireConfirm">
        <div class="popup-content confirm-modal">
          <div class="popup-header">
            <div>
              <div class="modal-title">Confirm Expire Current Pricing</div>
              <div class="modal-subtitle">Macola company {{ companyDb }}</div>
            </div>
            <button class="close-btn" @click="closeExpireConfirm">×</button>
          </div>

          <div class="panel warning-panel">
            This will update all current active pricing rows where end date is
            <b>12/31/2040</b> and set their end date to
            <b>{{ oldEndDateLabel }}</b>.
          </div>

          <div class="confirm-grid">
            <div>
              <div class="confirm-label">Rows expected</div>
              <div class="confirm-value">{{ formatNumber(activePreview?.active_count) }}</div>
            </div>
            <div>
              <div class="confirm-label">Effective date</div>
              <div class="confirm-value">{{ effectiveDt }}</div>
            </div>
            <div>
              <div class="confirm-label">Old rows expire</div>
              <div class="confirm-value">{{ oldEndDateLabel }}</div>
            </div>
          </div>

          <div class="control" style="margin-top:12px;">
            <label>Type EXPIRE to confirm</label>
            <input v-model="expireConfirmText" type="text" />
          </div>

          <div v-if="modalError" class="panel error-box" style="margin-top:10px;">
            {{ modalError }}
          </div>

          <div class="modal-actions">
            <button
              class="plan-it-btn danger-btn"
              :disabled="expireLoading || expireConfirmText !== 'EXPIRE'"
              @click="expireAllActivePricing"
            >
              {{ expireLoading ? "Expiring..." : "Expire Pricing" }}
            </button>

            <button
              class="plan-it-btn"
              :disabled="expireLoading"
              @click="closeExpireConfirm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <!-- Insert Confirmation Modal -->
      <div v-if="showInsertConfirm" class="popup-overlay" @click.self="closeInsertConfirm">
        <div class="popup-content confirm-modal">
          <div class="popup-header">
            <div>
              <div class="modal-title">Confirm Insert New Pricing</div>
              <div class="modal-subtitle">Macola company {{ companyDb }}</div>
            </div>
            <button class="close-btn" @click="closeInsertConfirm">×</button>
          </div>

          <div class="panel warning-panel">
            This will clone the expired rows ending <b>{{ oldEndDateLabel }}</b>,
            increase price breaks by <b>{{ formattedPct }}</b>, set start date to
            <b>{{ effectiveDt }}</b>, and set end date to <b>12/31/2040</b>.
          </div>

          <div class="confirm-grid">
            <div>
              <div class="confirm-label">Rows expected</div>
              <div class="confirm-value">{{ formatNumber(expiredPreview?.expired_count) }}</div>
            </div>
            <div>
              <div class="confirm-label">Increase</div>
              <div class="confirm-value">{{ formattedPct }}</div>
            </div>
            <div>
              <div class="confirm-label">New active end</div>
              <div class="confirm-value">2040-12-31</div>
            </div>
          </div>

          <div class="control" style="margin-top:12px;">
            <label>Type INSERT to confirm</label>
            <input v-model="insertConfirmText" type="text" />
          </div>

          <div v-if="modalError" class="panel error-box" style="margin-top:10px;">
            {{ modalError }}
          </div>

          <div class="modal-actions">
            <button
              class="plan-it-btn danger-btn"
              :disabled="insertLoading || insertConfirmText !== 'INSERT'"
              @click="insertAllActivePricingIncrease"
            >
              {{ insertLoading ? "Inserting..." : "Insert Pricing" }}
            </button>

            <button
              class="plan-it-btn"
              :disabled="insertLoading"
              @click="closeInsertConfirm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "PricingIncreaseAdminPage",

  data() {
    return {
      // Locked to LIVE company in backend route
      companyDb: "100",

      // Defaults for the planned increase
      pct: "8",
      effectiveDt: "2026-06-08",

      error: null,
      success: null,
      modalError: null,

      activePreview: null,
      expiredPreview: null,
      finalPreview: null,

      previewLoading: false,
      expireLoading: false,
      expiredPreviewLoading: false,
      insertLoading: false,
      finalPreviewLoading: false,
      exportLoading: false,

      expiredDone: false,
      insertedDone: false,

      showExpireConfirm: false,
      showInsertConfirm: false,
      expireConfirmText: "",
      insertConfirmText: ""
    };
  },

  computed: {
    pctNum() {
      return Number(String(this.pct || "").trim());
    },

    inputsValid() {
      return (
        isFinite(this.pctNum) &&
        this.isValidYyyyMmDd(this.effectiveDt)
      );
    },

    formattedPct() {
      if (!isFinite(this.pctNum)) return "-";
      return `${this.pctNum}%`;
    },

    oldEndDateLabel() {
      if (!this.isValidYyyyMmDd(this.effectiveDt)) return "-";
      const d = new Date(this.effectiveDt + "T00:00:00");
      d.setDate(d.getDate() - 1);
      return d.toISOString().slice(0, 10);
    },

    anyLoading() {
      return (
        this.previewLoading ||
        this.expireLoading ||
        this.expiredPreviewLoading ||
        this.insertLoading ||
        this.finalPreviewLoading
      );
    },

    canExpire() {
      if (!this.inputsValid) return false;
      if (!this.activePreview) return false;
      if (this.expiredDone) return false;
      if (Number(this.activePreview.active_count || 0) <= 0) return false;
      if (Number(this.activePreview.future_count || 0) > 0) return false;
      return true;
    },

    canPreviewExpired() {
      if (!this.inputsValid) return false;
      return true;
    },

    canInsert() {
      if (!this.inputsValid) return false;
      if (!this.expiredPreview) return false;
      if (this.insertedDone) return false;
      if (Number(this.expiredPreview.expired_count || 0) <= 0) return false;
      if (Number(this.expiredPreview.active_new_count || 0) > 0) return false;
      return true;
    },

    currentStep() {
      if (!this.activePreview) return 1;
      if (!this.expiredDone) return 2;
      if (!this.expiredPreview) return 3;
      if (!this.insertedDone) return 4;
      return 5;
    }
  },

  methods: {
    clearMessages() {
      this.error = null;
      this.success = null;
      this.modalError = null;
    },

    resetPage() {
      this.error = null;
      this.success = null;
      this.modalError = null;

      this.activePreview = null;
      this.expiredPreview = null;
      this.finalPreview = null;

      this.expiredDone = false;
      this.insertedDone = false;

      this.showExpireConfirm = false;
      this.showInsertConfirm = false;
      this.expireConfirmText = "";
      this.insertConfirmText = "";
    },

    validateInputs() {
      if (!isFinite(this.pctNum)) {
        this.error = "Percent increase must be a valid number.";
        return false;
      }

      if (!this.isValidYyyyMmDd(this.effectiveDt)) {
        this.error = "Effective date must be a valid YYYY-MM-DD date.";
        return false;
      }

      return true;
    },

    async previewAllActivePricing() {
      this.clearMessages();

      if (!this.validateInputs()) return;

      this.previewLoading = true;
      this.activePreview = null;
      this.expiredPreview = null;
      this.finalPreview = null;
      this.expiredDone = false;
      this.insertedDone = false;

      try {
        const { data } = await axios.get("/previewAllActivePricingIncrease", {
          params: {
            pct: this.pctNum,
            effective_dt: this.effectiveDt
          }
        });

        this.activePreview = data;
        this.success = `Preview loaded for ${this.formatNumber(data.active_count)} active pricing rows.`;

      } catch (err) {
        const payload = err?.response?.data;
        this.error =
          payload?.error ||
          payload?.details ||
          "Failed to preview active pricing increase.";
      } finally {
        this.previewLoading = false;
      }
    },

    openExpireConfirm() {
      this.modalError = null;
      this.expireConfirmText = "";
      this.showExpireConfirm = true;
    },

    closeExpireConfirm() {
      if (this.expireLoading) return;
      this.showExpireConfirm = false;
      this.expireConfirmText = "";
      this.modalError = null;
    },

    async expireAllActivePricing() {
      this.modalError = null;
      this.error = null;
      this.success = null;

      if (!this.canExpire) {
        this.modalError = "Expire is not available. Run preview and resolve any warnings first.";
        return;
      }

      if (this.expireConfirmText !== "EXPIRE") {
        this.modalError = "Type EXPIRE to confirm.";
        return;
      }

      this.expireLoading = true;

      try {
        const { data } = await axios.post("/expireAllActivePricing", {
          effective_dt: this.effectiveDt
        });

        this.expiredDone = true;
        this.showExpireConfirm = false;
        this.expireConfirmText = "";

        this.success = `Expired ${this.formatNumber(data.expired_rows)} active pricing rows.`;

        await this.previewExpiredPricing();

      } catch (err) {
        const payload = err?.response?.data;
        this.modalError =
          payload?.error ||
          payload?.details ||
          "Failed to expire active pricing.";
      } finally {
        this.expireLoading = false;
      }
    },

    async previewExpiredPricing() {
      this.clearMessages();

      if (!this.validateInputs()) return;

      this.expiredPreviewLoading = true;
      this.expiredPreview = null;
      this.finalPreview = null;

      try {
        const { data } = await axios.get("/previewExpiredPricingIncrease", {
          params: {
            pct: this.pctNum,
            effective_dt: this.effectiveDt
          }
        });

        this.expiredPreview = data;
        this.success = `Expired preview loaded for ${this.formatNumber(data.expired_count)} rows.`;

      } catch (err) {
        const payload = err?.response?.data;
        this.error =
          payload?.error ||
          payload?.details ||
          "Failed to preview expired pricing increase.";
      } finally {
        this.expiredPreviewLoading = false;
      }
    },

    openInsertConfirm() {
      this.modalError = null;
      this.insertConfirmText = "";
      this.showInsertConfirm = true;
    },

    closeInsertConfirm() {
      if (this.insertLoading) return;
      this.showInsertConfirm = false;
      this.insertConfirmText = "";
      this.modalError = null;
    },

    async insertAllActivePricingIncrease() {
      this.modalError = null;
      this.error = null;
      this.success = null;

      if (!this.canInsert) {
        this.modalError = "Insert is not available. Preview expired rows and resolve any warnings first.";
        return;
      }

      if (this.insertConfirmText !== "INSERT") {
        this.modalError = "Type INSERT to confirm.";
        return;
      }

      this.insertLoading = true;

      try {
        const { data } = await axios.post("/insertAllActivePricingIncrease", {
          pct: this.pctNum,
          effective_dt: this.effectiveDt
        });

        this.insertedDone = true;
        this.showInsertConfirm = false;
        this.insertConfirmText = "";

        this.success = `Inserted ${this.formatNumber(data.inserted_rows)} new active pricing rows.`;

        await this.previewFinalActivePricing();

      } catch (err) {
        const payload = err?.response?.data;
        this.modalError =
          payload?.error ||
          payload?.details ||
          "Failed to insert new pricing.";
      } finally {
        this.insertLoading = false;
      }
    },

    async previewFinalActivePricing() {
      this.clearMessages();

      if (!this.isValidYyyyMmDd(this.effectiveDt)) {
        this.error = "Effective date must be a valid YYYY-MM-DD date.";
        return;
      }

      this.finalPreviewLoading = true;
      this.finalPreview = null;

      try {
        const { data } = await axios.get("/previewFinalActivePricingIncrease", {
          params: {
            effective_dt: this.effectiveDt
          }
        });

        this.finalPreview = data;
        this.success = `Final preview loaded for ${this.formatNumber(data.final_active_count)} new active pricing rows.`;

      } catch (err) {
        const payload = err?.response?.data;
        this.error =
          payload?.error ||
          payload?.details ||
          "Failed to preview final active pricing.";
      } finally {
        this.finalPreviewLoading = false;
      }
    },

    async exportActivePricingExcel() {
      this.clearMessages();
      this.exportLoading = true;

      try {
        const response = await axios.get("/exportActivePricingExcel", {
          params: {
            company_db: "both"
          },
          responseType: "blob"
        });

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        const today = new Date().toISOString().slice(0, 10);
        link.href = url;
        link.download = `active_pricing_2040_DB_100_500_${today}.xlsx`;

        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);

        this.success = "Excel export downloaded for DB 100 and DB 500 active pricing.";

      } catch (err) {
        const payload = err?.response?.data;

        this.error =
          payload?.error ||
          payload?.details ||
          "Failed to export active pricing Excel file.";
      } finally {
        this.exportLoading = false;
      }
    },

    isValidYyyyMmDd(s) {
      if (!s) return false;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;

      const d = new Date(s + "T00:00:00");
      if (isNaN(d.getTime())) return false;

      return d.toISOString().slice(0, 10) === s;
    },

    formatDate(v) {
      if (!v) return "";
      const d = new Date(v);
      if (isNaN(d.getTime())) {
        const s = String(v);
        return s.slice(0, 10);
      }
      return d.toISOString().slice(0, 10);
    },

    formatMoney(v) {
      const num = Number(v);
      if (!isFinite(num)) return "-";
      return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatNumber(v) {
      const num = Number(v);
      if (!isFinite(num)) return "0";
      return num.toLocaleString(undefined, {
        maximumFractionDigits: 0
      });
    },

    rowKey(r) {
      return [
        r?.cust_no || "",
        r?.item_no || "",
        r?.prod_cat || "",
        r?.cd_prc_basis || "",
        r?.start_dt || "",
        r?.end_dt || ""
      ].join("-");
    }
  }
};
</script>

<style scoped>

/* PAGE BASE — Pablo black */
.pricing-admin-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

/* BASE CONTAINER — matches JIMScheduling / PriceSheetPage vibe */
.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #0b0101;
  min-height: 100vh;
}

/* Header row */
.page-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 10px;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin: 0;
  text-align: center;
}

/* MAIN LAYOUT */
.main-content {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

/* Left-side controls */
.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 80px;
}

/* Right-side workspace */
.right-side-section {
  width: 100%;
  min-width: 0;
}

/* Scrollable area */
.sheet-scroll-container {
  max-height: 72vh;
  overflow-y: auto;
  width: 100%;
  border-radius: 10px;
  background-color: #0b0101;
  padding-right: 10px;
  box-sizing: border-box;
}

.sheet-scroll-container::-webkit-scrollbar {
  width: 10px;
}

.sheet-scroll-container::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 8px;
}

.sheet-scroll-container::-webkit-scrollbar-track {
  background: #111;
}

/* Pablo dark panel */
.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}

/* Controls */
.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control input {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

.control input:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

/* Pablo buttons */
.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 170px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
  height: 38px;
}

.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

.danger-btn {
  background-color: #fee2e2;
  border-color: #fca5a5;
  color: #7f1d1d;
}

.danger-btn:hover:not(:disabled) {
  background-color: #fecaca;
  border-color: #f87171;
}

/* Error / success */
.error-box {
  border-color: #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
  width: 100%;
}

.success-box {
  border-color: #4b9b63;
  background: #0f2818;
  color: #b7f7c5;
  width: 100%;
}

.inline-message {
  width: auto;
  margin-bottom: 10px;
}

/* Warning panel */
.warning-panel {
  margin-bottom: 12px;
  background: #1a1304;
  border-color: #a16207;
  color: #fde68a;
}

.warning-title {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 4px;
}

.warning-body {
  font-size: 14px;
  line-height: 1.35;
}

/* Summary bar */
.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background-color: #708090a4;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 140px;
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 18px;
  opacity: 0.85;
  line-height: 1.1;
}

.summary-value {
  font-size: 24px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
  margin-top: 2px;
}

/* Step panel */
.step-panel {
  margin-bottom: 12px;
  background: rgba(255,255,255,0.04);
}

.step-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(120px, 1fr));
  gap: 10px;
}

.step {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  border: 1px solid #333;
  border-radius: 10px;
  padding: 8px;
  background: #0f141c;
  opacity: 0.75;
}

.step.active {
  border-color: #cbbfbf;
  opacity: 1;
}

.step.done {
  border-color: #4b9b63;
  background: #0f2818;
  opacity: 1;
}

.step-num {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111;
  border: 1px solid #555;
  color: #fff;
  font-weight: bold;
  flex: 0 0 auto;
}

.step-title {
  color: #fff;
  font-weight: bold;
  font-size: 13px;
}

.step-sub {
  font-size: 11px;
  line-height: 1.25;
  opacity: 0.8;
}

/* Preview panels */
.preview-panel {
  background-color: #708090a4;
  border-radius: 10px;
  border: 1px solid #888;
  margin-bottom: 14px;
}

.sheet-page-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}

.sheet-page-header h2 {
  margin: 0;
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 22px;
  color: #fff;
}

.small-muted {
  font-size: 12px;
  opacity: 0.8;
  color: #fff;
}

.summary-totals-bar {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: stretch;
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
}

.summary-totals-bar .tot {
  min-width: 120px;
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.08);
}

.summary-totals-bar .k {
  font-size: 11px;
  letter-spacing: 0.4px;
  opacity: 0.75;
  margin-bottom: 4px;
}

.summary-totals-bar .v {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
}

.badText {
  color: #fca5a5 !important;
}

/* Table */
.table-scroll {
  overflow-x: auto;
  padding-bottom: 6px;
}

.popup-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  min-width: 1000px;
}

.popup-table th,
.popup-table td {
  border: 1px solid #333;
  padding: 8px;
  font-size: 13px;
  text-align: left;
}

.popup-table th {
  background: #111827;
  color: #cbbfbf;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 3;
}

.popup-table tr:nth-child(even) td {
  background: #0f141c;
}

.empty-row {
  padding: 14px;
  font-size: 14px;
  opacity: 0.75;
  text-align: center !important;
}

/* Empty state */
.empty-state {
  background-color: #708090a4;
  color: #fff;
  font-size: 16px;
  line-height: 1.45;
}

/* Popup modal */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background-color: #0b0101;
  padding: 10px 20px;
  border-radius: 8px;
  text-align: left;
  max-width: 95vw;
  max-height: 90vh;
  overflow: auto;
  border: 1px solid #888;
  color: #cbbfbf;
}

.confirm-modal {
  width: 600px;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;
}

.close-btn {
  background: transparent;
  border: none;
  color: #cbbfbf;
  font-size: 20px;
  cursor: pointer;
  font-weight: bold;
}

.modal-title {
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 22px;
  font-weight: bold;
  color: #fff;
}

.modal-subtitle {
  font-size: 13px;
  opacity: 0.85;
}

.confirm-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.confirm-grid > div {
  border: 1px solid #333;
  border-radius: 8px;
  padding: 8px 10px;
  background: #0f141c;
}

.confirm-label {
  font-size: 12px;
  opacity: 0.8;
}

.confirm-value {
  font-size: 16px;
  font-weight: bold;
  color: #fff;
  margin-top: 3px;
}

.modal-actions {
  margin-top: 14px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

/* Responsive */
@media (max-width: 1000px) {
  .main-content {
    grid-template-columns: 1fr;
  }

  .left-side-section {
    margin-top: 10px;
    align-items: stretch;
  }

  .plan-it-btn {
    width: 100%;
  }

  .step-row {
    grid-template-columns: 1fr;
  }
}

</style>